import { KeyValueLabel, NoValue } from '@tribeauto/ngx-mobilite-material';
import { UserTablePreferences } from '../preferences/preferences.model';
import { posAll } from '../../../core/helpers/point-of-sale.helper';
import { sellerAll } from '../../../core/helpers/seller.helper';

export const creditProductCodes = ['VAT', 'VAP'];

export type RecordsRequest = {
  partnerInfo: {
    chainCodes?: string[];
    groupCodes?: string[];
    societyCodes?: string[];
    unionCodes?: string[];
    webStoreIds: string[];
  };
  userLogin?: string | null;
  cckNumbers?: string[];
  names?: string[];
  unreadMessage?: boolean;
  caller: string;
};

export type PointOfSale = {
  chainCode?: string;
  groupCode?: string;
  societyCode?: string;
  unionCode?: string;
  webStoreId?: string;
  webStoreName: string;
};

export type Vehicle = {
  model: string;
  version: string;
  brand: string;
  used: boolean;
};

export type Insurance = {
  category: string;
  code: string;
  installmentAmount: number;
  totalAmount: number;
  vendor: string;
};

export type MyFile = {
  duration: number;
  creationDate: Date;
  installmentAmountWithInsurances: number;
  pricingLabel: string;
  userLogin: string;
  prospectName: string;
  cckNumber: string;
  pointOfSale: PointOfSale;
  vehicle: Vehicle;
  insurances: Insurance[];
  overdraftAmount: number;
  siclidProductCode: string;
  uploadStatus: string;
  signStatus: string;
  status: string;
  subStatus: string;
  unreadMessages: number;
  rvDerogationStatus: string | NoValue;
  residualValueAmount: number | NoValue;
  signature: Signature;
  vehicleDeliveryDate: Date;
  instalmentAmountWithoutInsurances: number;
  residualValuePercent: number;
  actions: string[];
};

export type Signature = {
  statusSigned: boolean;
  type: number | NoValue;
  pfStatusSigned: boolean;
  deliverystatusSigned: boolean;
};

export type RecordsResponse = {
  myFileResponses: MyFile[];
} & PaginationData;

export type FileRowData = {
  creationDate: string;
  cckNumber: string;
  unreadMessages: number;
  prospectName: string;
  siclidProductCode: string;
  insurances: number;
  statusSigned: string;
  subStatus: string;
  rvDerogationStatus: string;
  brand: string;
  duration: number;
  instalmentAmountWithInsurances: string;
  category: string;
  pricingLabel: string;
  residualValueAmount: string;
  webStoreId: string;
  webStoreName: string;
  userLogin: string;
  vehicleDeliveryDate: string;
  overdraftAmount: string;
  instalmentAmountWithoutInsurances: string;
  used: string;
  model: string;
  version: string;
  residualValuePercent: string;
  type: string | null;
  pfStatusSigned: string | null;
  deliverystatusSigned: string | null;
  actions: string[];
  details: DetailsRowData;
};

export enum RecordsColumnsEnum {
  creationDate = 'creationDate',
  cckNumber = 'cckNumber',
  unreadMessages = 'unreadMessages',
  prospectName = 'prospectName',
  siclidProductCode = 'siclidProductCode',
  insurances = 'insurances',
  statusSigned = 'statusSigned',
  subStatus = 'subStatus',
  rvDerogationStatus = 'rvDerogationStatus',
  brand = 'brand',
  duration = 'duration',
  instalmentAmountWithInsurances = 'instalmentAmountWithInsurances',
  category = 'category',
  pricingLabel = 'pricingLabel',
  residualValueAmount = 'residualValueAmount',
  webStoreId = 'webStoreId',
  webStoreName = 'webStoreName',
  userLogin = 'userLogin',
  vehicleDeliveryDate = 'vehicleDeliveryDate',
  overdraftAmount = 'overdraftAmount',
  instalmentAmountWithoutInsurances = 'instalmentAmountWithoutInsurances',
  used = 'used',
  model = 'model',
  version = 'version',
  residualValuePercent = 'residualValuePercent',
  type = 'type',
  pfStatusSigned = 'pfStatusSigned',
  deliverystatusSigned = 'deliverystatusSigned',
  actions = 'actions',
  details = 'details'
}

export type DetailsRowData = {
  brand: string;
  duration: string;
  installmentWithInsurances: string;
  subscribedInsurances: string;
  pricingLabel: string;
  residualValue?: string;
};

export interface RecordsFilterData extends LeadsFilterData {
  seller: KeyValueLabel;
  unreadMessage: boolean;
}

export interface LeadsFilterData {
  pos: KeyValueLabel;
  cckNumber: string;
  name: string;
}

export const DEFAULT_FILTERS: RecordsFilterData = {
  pos: posAll,
  seller: sellerAll,
  cckNumber: '',
  name: '',
  unreadMessage: false
};

export const DEFAULT_FILTERS_LEADS: LeadsFilterData = {
  pos: posAll,
  cckNumber: '',
  name: ''
};

export type PaginationData = {
  page: number;
  size: number;
  totalPages: number;
  numberOfElements: number;
  hasNext: boolean;
  first: boolean;
  last: boolean;
};

export enum ReasonType {
  CANCELLED_ORDER = '001',
  COMPETITION = '002',
  DUPLICATE = '003',
  CASH_PAYMENT = '004',
  OTHER = '005'
}

export type UserSearchRequest = {
  tveiNames: string[] | null;
  isExternal: boolean | null;
};

export type AvailableSellerResponse = {
  jossoLogin: string;
  firstName: string;
  lastName: string;
};

export const baseUserRecordsPreferences: UserTablePreferences = {
  columnsOrder: {
    [RecordsColumnsEnum.creationDate]: 1,
    [RecordsColumnsEnum.cckNumber]: 2,
    [RecordsColumnsEnum.unreadMessages]: 3,
    [RecordsColumnsEnum.prospectName]: 4,
    [RecordsColumnsEnum.siclidProductCode]: 5,
    [RecordsColumnsEnum.insurances]: 6,
    [RecordsColumnsEnum.statusSigned]: 7,
    [RecordsColumnsEnum.subStatus]: 8,
    [RecordsColumnsEnum.rvDerogationStatus]: 9,
    [RecordsColumnsEnum.brand]: 10,
    [RecordsColumnsEnum.duration]: 11,
    [RecordsColumnsEnum.instalmentAmountWithInsurances]: 12,
    [RecordsColumnsEnum.category]: 13,
    [RecordsColumnsEnum.pricingLabel]: 14,
    [RecordsColumnsEnum.residualValueAmount]: 15,
    [RecordsColumnsEnum.webStoreId]: 16,
    [RecordsColumnsEnum.webStoreName]: 17,
    [RecordsColumnsEnum.userLogin]: 18,
    [RecordsColumnsEnum.vehicleDeliveryDate]: 19,
    [RecordsColumnsEnum.overdraftAmount]: 20,
    [RecordsColumnsEnum.instalmentAmountWithoutInsurances]: 21,
    [RecordsColumnsEnum.used]: 22,
    [RecordsColumnsEnum.model]: 23,
    [RecordsColumnsEnum.version]: 24,
    [RecordsColumnsEnum.residualValuePercent]: 25,
    [RecordsColumnsEnum.type]: 26,
    [RecordsColumnsEnum.pfStatusSigned]: 27,
    [RecordsColumnsEnum.deliverystatusSigned]: 28
  },
  columnsDisplayed: [
    RecordsColumnsEnum.creationDate,
    RecordsColumnsEnum.cckNumber,
    RecordsColumnsEnum.unreadMessages,
    RecordsColumnsEnum.prospectName,
    RecordsColumnsEnum.siclidProductCode,
    RecordsColumnsEnum.insurances,
    RecordsColumnsEnum.statusSigned,
    RecordsColumnsEnum.subStatus,
    RecordsColumnsEnum.rvDerogationStatus
  ]
};

export enum RecordsSortProperty {
  CREATION_DATE = 'transmissionDate',
  CCK_NUMBER = 'cckNumber',
  PROSPECT_NAME = 'client',
  SICLID_PRODUCT_CODE = 'product',
  INSURANCES = 'insurance',
  STATUS_SIGNED = 'signatureStatus',
  SUB_STATUS = 'status',
  RV_DEROGATION_STATUS = 'rvDerogationStatus',
  WEBSTORE_ID = 'pointOfSale',
  USER_LOGIN = 'vendor',
  OVERDRAFT_AMOUNT = 'overdraft',
  MODEL = 'vehicleModel',
  USED = 'used'
}
